import React, { useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMe } from './store/slices/authSlice';
import { subscribePush } from './services/pushNotifications';
import { attemptOpenInApp } from './utils/openInApp';

import Navbar from './components/common/Navbar';

import ForgotPassword from './pages/ForgotPassword';
import Logout from './pages/Logout';
import VerifyEmail from './pages/VerifyEmail';
import Profile from './pages/Profile';
import GaragePage from './pages/Garage';
import VehicleDetail from './pages/CarDetails';
import Events from './pages/Events';
import Collections from './pages/Discover';
import Search from './pages/Search';
import RoutesPage from './pages/Routes';
import Services from './pages/Services';
import Settings from './pages/Settings';
import Messages from './pages/Messages';
import NotFound from './pages/NotFound';
import OAuthCallback from './pages/OAuthCallback';
import OnboardingUsername from './pages/OnboardingUsername';
import Marketplace from './pages/Marketplace';
import MarketplaceListing from './pages/MarketplaceListing';
import MarketplaceCreate from './pages/MarketplaceCreate';
import MarketplaceSaved from './pages/MarketplaceSaved';
import Feed from './pages/Feed';
import Support from './pages/Support';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';

// The public landing page pulls in Leaflet and its baked route geometry, none of
// which the authenticated app shell needs — keep it in its own chunk.
const Landing = React.lazy(() => import('./pages/Landing'));
// Lazy so the picker's data bundle never loads for the rest of the site.
const PartPicker = React.lazy(() => import('./pages/PartPicker'));

// Dev-only 9:16 recording stages for the video outro clips (see pages/Outro.jsx).
// Gated on NODE_ENV so production builds drop both the routes and the chunks.
// Outro2 is the South-Florida-only cut for the Miami video — separate page,
// separate data module, no overlap with the AU/NZ rig or the landing hero.
const Outro =
  process.env.NODE_ENV === 'development' ? React.lazy(() => import('./pages/Outro')) : null;
const Outro2 =
  process.env.NODE_ENV === 'development' ? React.lazy(() => import('./pages/Outro2')) : null;

const AuthRoute = ({ children, allowDuringOnboarding = false }) => {
  const { isAuthenticated, loading, user } = useSelector(s => s.auth);
  const location = useLocation();
  if (loading) return <div className="loading-page"><div className="loading-spinner" /></div>;
  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} />;
  }
  // First-time OAuth signups land here without a chosen username — force the
  // picker before anything else loads (the onboarding route itself opts out).
  if (!allowDuringOnboarding && user && user.usernameSetByUser === false) {
    return <Navigate to="/onboarding/username" replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useSelector(s => s.auth);
  return isAuthenticated ? <Navigate to="/" /> : children;
};

// /login and /register no longer have pages of their own — signing in and
// signing up both live in the landing page's closing panel. The routes stay so
// the many places that still send people here (the 401 interceptor, logout,
// OAuth failures, password reset, store listings, old emails) don't 404.
//
// The search string rides along so AuthRoute's ?returnTo= survives the hop and
// the visitor still lands on the page they were chasing, and the #download
// hash drops them on the panel instead of the top of a long marketing page.
const AuthRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/${search}#download`} replace />;
};

const PAGE_BACKGROUNDS = {
  '/garage':       { file: 'garage' },
  '/profile':      { file: 'profile', brightness: 0.75, position: 'center center', scale: 1.02 },
  '/events':        { file: 'events' },
  '/marketplace':  { file: 'marketplace' },
  '/routes':       { file: 'routes' },
  '/collections':  { file: 'discover' },
  '/feed':         { file: 'yuhh', brightness: 0.4 },
  '/search':       { file: 'again', ext: 'png', brightness: 0.85, position: 'center 80%', scale: 1.0 },
  '/settings':     { file: 'settings' },
  '/messages':     { file: 'yuhh', brightness: 0.4 },
  '/vehicle':      { file: 'garage' },
};

const useBgConfig = () => {
  const { pathname } = useLocation();
  const key = Object.keys(PAGE_BACKGROUNDS).find(k => pathname.startsWith(k));
  const cfg = key ? PAGE_BACKGROUNDS[key] : { file: 'yuhh' };
  return {
    url: `/images/${cfg.file}.${cfg.ext ?? 'jpg'}`,
    brightness: cfg.brightness ?? 0.55,
    position: cfg.position ?? 'center',
    scale: cfg.scale ?? 1.02,
  };
};

const AppBackground = () => {
  const { url, brightness, position, scale } = useBgConfig();
  return (
    <>
      <div key={url} style={{
        position: 'fixed', inset: 0, zIndex: -2,
        backgroundImage: `url(${url})`,
        backgroundSize: 'cover', backgroundPosition: position,
        filter: `blur(5px) brightness(${brightness})`,
        transform: `scale(${scale})`,
      }} />
      <div style={{
        position: 'fixed', inset: 0, zIndex: -1, pointerEvents: 'none',
        background: 'rgba(0,0,0,0.18)',
      }} />
    </>
  );
};

const App = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector(s => s.auth);
  const { pathname } = useLocation();

  // The landing page is the logged-out view of "/". It brings its own floating
  // nav and dark canvas, so it opts out of both the reserved navbar space and
  // the blurred photo background the authenticated pages share.
  const isLanding = pathname === '/' && !isAuthenticated;

  const isPicker = pathname === '/partpicker' || pathname.startsWith('/partpicker/');

  // The landing hero is position:sticky, and the global
  // `html, body { overflow-x: hidden }` makes BODY a scroll container
  // (overflow-y computes to auto), which binds the sticky to an element that
  // never actually scrolls — so it never sticks. While the landing page is up,
  // swap both to `clip`, which clips identically without creating a scroll
  // container. Class-scoped so every authenticated page keeps `hidden`.
  useEffect(() => {
    if (!isLanding) return undefined;
    document.documentElement.classList.add('landing-sticky');
    document.body.classList.add('landing-sticky');
    return () => {
      document.documentElement.classList.remove('landing-sticky');
      document.body.classList.remove('landing-sticky');
    };
  }, [isLanding]);

  // Shared links (e.g. /events?event=ID) should hand off to the native app
  // before anything else — including the auth gate, which would otherwise bounce
  // a logged-out visitor to /login and never attempt the app open. Runs once on
  // first load using the raw URL so it fires regardless of which route renders.
  useEffect(() => {
    attemptOpenInApp(window.location.pathname, window.location.search);
  }, []);

  useEffect(() => {
    if (isAuthenticated) dispatch(fetchMe());
  }, []);

  useEffect(() => {
    if (isAuthenticated) subscribePush();
  }, [isAuthenticated]);

  // The picker is a standalone surface: no navbar, no background, no site chrome.
  // AppBackground/Navbar/main-content all sit OUTSIDE <Routes>, so a <Route> alone
  // can't escape them — it has to return before the app container is built. This
  // also sidesteps the "*" catch-all below, which would otherwise bounce a
  // logged-out visitor arriving on a picker URL straight back to the landing page.
  // Placed after every hook so the hook order stays identical on both branches.
  if (isPicker) {
    return (
      <Suspense fallback={<div className="loading-page"><div className="loading-spinner" /></div>}>
        <PartPicker />
      </Suspense>
    );
  }

  return (
    <div className={isLanding ? 'app-container app-container--landing' : 'app-container'}>
      {!isLanding && <AppBackground />}
      <Navbar />
      <main className={isLanding ? 'main-content main-content--flush' : 'main-content'}>
        <Suspense fallback={<div className="loading-page"><div className="loading-spinner" /></div>}>
        <Routes>
          <Route path="/login" element={<PublicRoute><AuthRedirect /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><AuthRedirect /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/oauth-callback" element={<OAuthCallback />} />
          <Route path="/support" element={<Support />} />
          {/* Superseded by the landing page at "/" — kept so existing links,
              store listings and the sign-in footer link don't 404. */}
          <Route path="/about" element={<Navigate to="/" replace />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/logout" element={<Logout />} />

          <Route path="/onboarding/username" element={<AuthRoute allowDuringOnboarding><OnboardingUsername /></AuthRoute>} />

          <Route path="/" element={isAuthenticated ? <Navigate to="/events" replace /> : <Landing />} />
          <Route path="/profile/:username" element={<AuthRoute><Profile /></AuthRoute>} />
          <Route path="/garage/:userId" element={<AuthRoute><GaragePage /></AuthRoute>} />
          <Route path="/vehicle/:id" element={<AuthRoute><VehicleDetail /></AuthRoute>} />
          <Route path="/feed" element={<AuthRoute><Feed /></AuthRoute>} />
          <Route path="/events" element={<AuthRoute><Events /></AuthRoute>} />
          <Route path="/collections" element={<AuthRoute><Collections /></AuthRoute>} />
          <Route path="/search" element={<AuthRoute><Search /></AuthRoute>} />
          <Route path="/marketplace" element={<AuthRoute><Marketplace /></AuthRoute>} />
          <Route path="/marketplace/create" element={<AuthRoute><MarketplaceCreate /></AuthRoute>} />
          <Route path="/marketplace/edit/:id" element={<AuthRoute><MarketplaceCreate /></AuthRoute>} />
          <Route path="/marketplace/saved" element={<AuthRoute><MarketplaceSaved /></AuthRoute>} />
          <Route path="/marketplace/:id" element={<AuthRoute><MarketplaceListing /></AuthRoute>} />
          <Route path="/routes" element={<AuthRoute><RoutesPage /></AuthRoute>} />
          <Route path="/services" element={<AuthRoute><Services /></AuthRoute>} />
          <Route path="/settings" element={<AuthRoute><Settings /></AuthRoute>} />
          <Route path="/messages" element={<AuthRoute><Messages /></AuthRoute>} />

          {Outro && <Route path="/outro" element={<Outro />} />}
          {Outro2 && <Route path="/outro2" element={<Outro2 />} />}

          {/* Logged-out visitors used to be dumped on the login form from any
              unknown URL; send them to the landing page instead. */}
          <Route path="*" element={isAuthenticated ? <NotFound /> : <Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>
    </div>
  );
};

export default App;
